import React, { useState } from 'react';
import { X, CheckCircle2, Circle, Clock, Gift, Trash2, PackageCheck, Printer } from 'lucide-react';
import { printPackingSlip } from './packingSlip';

export const PRESETS = {
  Pack: { length: 15, width: 25, height: 5, weight: 0.25 },
  Box: { length: 17, width: 27, height: 10, weight: 0.25 },
};

export default function PackFocusMode({
  order, position, total, products, slipNote,
  packed, itemKey,
  onToggleItem, onTogglePreorder, onAddFreebie, onRemoveFreebie,
  pkg, onPreset, onPkgField, onPkgCommit,
  onComplete, onClose, busy, onHold,
}) {
  const cust = order.customer || {};
  const physical = (order.items || []).filter((it) => !it.digital && (it.qty || 0) > 0);
  const required = physical.filter((it) => !it.preorder);
  const done = required.filter((it) => packed[itemKey(order.orderNumber, it)]).length;
  const allDone = required.length > 0 && done === required.length;
  const allPreorder = physical.length > 0 && required.length === 0;
  const hasPreorder = physical.some((it) => it.preorder);

  const [showFreebie, setShowFreebie] = useState(false);
  const [freebieId, setFreebieId] = useState('');
  const [freebieQty, setFreebieQty] = useState(1);
  const [addingFreebie, setAddingFreebie] = useState(false);

  const addFreebie = async () => {
    if (!freebieId) return;
    setAddingFreebie(true);
    await onAddFreebie(order.orderNumber, freebieId, Math.max(1, Number(freebieQty) || 1));
    setAddingFreebie(false);
    setFreebieId(''); setFreebieQty(1); setShowFreebie(false);
  };

  const d = pkg || { type: 'Pack', ...PRESETS.Pack };
  const numInput = 'w-full bg-alp-dark border border-alp-line rounded px-2 py-2.5 text-alp-cream text-base text-center outline-none focus:border-alp-gold';

  return (
    <div className="fixed inset-0 z-50 bg-alp-black overflow-y-auto" data-testid="pack-focus-mode">
      <div className="max-w-2xl mx-auto px-4 pt-5 pb-44">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <span className="text-alp-gray text-xs tracking-[0.2em] uppercase">Packing order <span className="text-alp-gold font-semibold">{position}</span> of {total}</span>
          <div className="flex items-center gap-2">
            <button onClick={() => printPackingSlip(order, slipNote)} data-testid="focus-print-slip-btn" className="inline-flex items-center gap-2 px-3 py-2 rounded border border-alp-gold/50 text-alp-gold text-[10px] tracking-[0.15em] uppercase hover:bg-alp-gold hover:text-alp-black transition-colors"><Printer size={14} /> Print slip</button>
            <button onClick={onClose} data-testid="focus-close" className="p-2 rounded border border-alp-line text-alp-gray hover:text-alp-cream hover:border-alp-gold transition-colors"><X size={18} /></button>
          </div>
        </div>

        {/* Customer */}
        <div className="border border-alp-gold/30 rounded-lg bg-alp-card/50 p-5 mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-serif text-3xl text-alp-cream leading-tight" data-testid="focus-customer-name">{cust.fullName || 'Customer'}</div>
              <div className="text-alp-gray text-sm mt-1">{[cust.address, cust.city, cust.state, cust.postcode].filter(Boolean).join(', ')}</div>
            </div>
            <span className="font-serif text-xl text-alp-gold shrink-0" data-testid="focus-order-number">{order.orderNumber}</span>
          </div>
        </div>

        {/* Items */}
        <p className="text-[11px] tracking-[0.2em] uppercase text-alp-gray mb-3">Tick each item as it goes in the parcel</p>
        <div className="space-y-3 mb-8">
          {physical.map((it) => {
            const key = itemKey(order.orderNumber, it);
            const isPacked = !!packed[key];
            const isPre = !!it.preorder;
            return (
              <div
                key={key}
                onClick={() => { if (!isPre) onToggleItem(order, it); }}
                data-testid={`focus-item-${it.id}`}
                className={`flex items-center gap-4 rounded-lg px-4 py-4 border cursor-pointer transition-colors select-none ${isPre ? 'bg-amber-500/5 border-amber-500/40 cursor-default' : isPacked ? 'bg-alp-gold/10 border-alp-gold/50' : 'bg-alp-card/40 border-alp-line hover:border-alp-gold/50'}`}
              >
                {isPre
                  ? <Clock size={26} className="text-amber-400 shrink-0" />
                  : isPacked
                    ? <CheckCircle2 size={26} className="text-alp-gold shrink-0" />
                    : <Circle size={26} className="text-alp-gray shrink-0" />}
                <span className={`font-serif text-3xl shrink-0 w-12 text-center ${isPre ? 'text-amber-400' : 'text-alp-gold'}`}>{it.qty}&times;</span>
                <div className="flex-1 min-w-0">
                  <div className={`text-lg leading-snug ${isPre ? 'text-amber-200/90' : isPacked ? 'text-alp-gold line-through' : 'text-alp-cream'}`}>{it.name}</div>
                  {it.size ? <div className="text-alp-gray text-sm">{it.size}</div> : null}
                  {isPre && <div className="text-amber-400 text-[11px] tracking-[0.12em] uppercase mt-0.5">Preorder &mdash; ships later, not in this parcel</div>}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); onTogglePreorder(order, it); }}
                  data-testid={`focus-preorder-${it.id}`}
                  className={`shrink-0 px-3 py-2 rounded text-[10px] tracking-[0.12em] uppercase border transition-colors ${isPre ? 'bg-amber-500/20 border-amber-500/60 text-amber-300' : 'border-alp-line text-alp-gray hover:border-amber-500/60 hover:text-amber-300'}`}
                >
                  {isPre ? 'Undo preorder' : 'Preorder'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Freebies */}
        <div className="border border-emerald-500/30 rounded-lg bg-emerald-500/5 p-4 mb-8" data-testid="focus-freebies">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
            <span className="inline-flex items-center gap-2 text-emerald-300 text-sm font-medium"><Gift size={16} /> Freebies thrown in</span>
            {!showFreebie && (
              <button onClick={() => setShowFreebie(true)} data-testid="focus-add-freebie-btn" className="px-3 py-1.5 rounded text-[10px] tracking-[0.12em] uppercase border border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/20 transition-colors">+ Add freebie</button>
            )}
          </div>
          <p className="text-alp-gray text-[11px] mb-3">Stock is deducted automatically so your counts stay right.</p>
          {(order.freebies || []).length > 0 && (
            <div className="space-y-2 mb-3">
              {(order.freebies || []).map((f) => (
                <div key={f.id} className="flex items-center gap-3 rounded px-3 py-2 bg-alp-dark/60 border border-emerald-500/20" data-testid={`focus-freebie-${f.id}`}>
                  <span className="font-serif text-lg text-emerald-300 shrink-0">{f.qty}&times;</span>
                  <span className="flex-1 text-alp-cream text-sm">{f.name}{f.size ? <span className="text-alp-gray text-xs ml-2">{f.size}</span> : null}</span>
                  <button onClick={() => onRemoveFreebie(order.orderNumber, f.id)} data-testid={`focus-freebie-remove-${f.id}`} className="p-1.5 text-alp-gray hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
          {showFreebie && (
            <div className="flex flex-wrap items-center gap-2">
              <select value={freebieId} onChange={(e) => setFreebieId(e.target.value)} data-testid="focus-freebie-product"
                className="flex-1 min-w-[180px] bg-alp-dark border border-alp-line rounded px-3 py-2.5 text-alp-cream text-sm outline-none focus:border-alp-gold">
                <option value="">Choose a product...</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name}{p.size ? ` (${p.size})` : ''} &mdash; {p.inventory ?? 0} in stock</option>)}
              </select>
              <input type="number" min="1" value={freebieQty} onChange={(e) => setFreebieQty(e.target.value)} data-testid="focus-freebie-qty"
                className="w-16 bg-alp-dark border border-alp-line rounded px-2 py-2.5 text-alp-cream text-sm text-center outline-none focus:border-alp-gold" />
              <button onClick={addFreebie} disabled={!freebieId || addingFreebie} data-testid="focus-freebie-add"
                className="px-4 py-2.5 rounded bg-emerald-500/80 text-alp-black text-xs tracking-[0.12em] uppercase font-semibold hover:bg-emerald-400 transition-colors disabled:opacity-40">{addingFreebie ? 'Adding...' : 'Add'}</button>
              <button onClick={() => setShowFreebie(false)} className="px-3 py-2.5 text-alp-gray text-xs hover:text-alp-cream transition-colors">Cancel</button>
            </div>
          )}
        </div>

        {/* Packaging */}
        <div className="border border-alp-line rounded-lg bg-alp-card/40 p-4" data-testid="focus-packaging">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <span className="text-[11px] tracking-[0.2em] uppercase text-alp-gray">Packaging (Australia Post)</span>
            <div className="flex gap-2">
              {['Pack', 'Box'].map((t) => (
                <button key={t} onClick={() => onPreset(order.orderNumber, t)} data-testid={`fpkg-${t.toLowerCase()}`}
                  className={`px-4 py-2 rounded text-[11px] tracking-[0.12em] uppercase border transition-colors ${d.type === t ? 'bg-alp-gold text-alp-black border-alp-gold' : 'text-alp-cream border-alp-line hover:border-alp-gold/60'}`}>{t}</button>
              ))}
              {d.type === 'Custom' && <span className="px-3 py-2 rounded text-[11px] tracking-[0.12em] uppercase border border-alp-gold/50 text-alp-gold">Custom</span>}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {[['length', 'L cm'], ['width', 'W cm'], ['height', 'H cm'], ['weight', 'kg']].map(([f, lbl]) => (
              <div key={f}>
                <label className="block text-[10px] text-alp-gray mb-1 text-center">{lbl}</label>
                <input type="number" step="0.01" value={d[f] ?? ''} data-testid={`fpkg-${f}`}
                  onChange={(e) => onPkgField(order.orderNumber, f, e.target.value)} onBlur={() => onPkgCommit(order.orderNumber)}
                  className={numInput} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sticky bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-10 bg-alp-black/95 border-t border-alp-line backdrop-blur px-4 py-4">
        <div className="max-w-2xl mx-auto">
          {allPreorder ? (
            <p className="text-amber-300 text-sm text-center mb-2" data-testid="focus-all-preorder-note">Every item is marked preorder &mdash; nothing to pack in this parcel yet.</p>
          ) : (
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-1 h-2 rounded-full bg-alp-line/50 overflow-hidden">
                <div className="h-full bg-alp-gold transition-all" style={{ width: `${required.length ? (done / required.length) * 100 : 0}%` }} />
              </div>
              <span className="text-alp-gray text-xs shrink-0" data-testid="focus-progress">{done} of {required.length} packed{physical.length - required.length > 0 ? ` \u00b7 ${physical.length - required.length} preorder` : ''}</span>
            </div>
          )}
          {hasPreorder && onHold && (
            <button onClick={() => onHold(order)} disabled={busy} data-testid="focus-preorder-later-btn"
              className="w-full mb-2 inline-flex items-center justify-center gap-2 rounded py-3 text-xs tracking-[0.2em] uppercase font-semibold border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 transition-colors disabled:opacity-50">
              <Clock size={15} /> Preorder &mdash; pack later &rarr; next order
            </button>
          )}
          <button
            onClick={() => onComplete(order)}
            disabled={!allDone || busy}
            data-testid="focus-complete-btn"
            className={`w-full inline-flex items-center justify-center gap-2 rounded py-4 text-sm tracking-[0.2em] uppercase font-semibold transition-colors ${allDone && !busy ? 'bg-alp-gold text-alp-black hover:bg-alp-goldlight' : 'bg-alp-card text-alp-gray cursor-not-allowed border border-alp-line'}`}
          >
            <PackageCheck size={18} /> {busy ? 'Saving...' : allDone ? 'Mark as Packed \u2192 next order' : 'Tick every item to finish'}
          </button>
        </div>
      </div>
    </div>
  );
}
